import React, { useState } from "react"
import { useAsync, useAsyncCallback } from "react-async-hook"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useApp } from "../../AppProvider"
import { useApi } from "../../hooks/useApi"
import { Organization } from "../../types/Organization"
import { Button } from "../common"

export const EditUser: React.FC = () => {
  const initialData = {
    email: "",
    password: "",
    password2: "",
    name: "",
    role: "",
  }
  const [formData, setFormData] = useState<any>(initialData)
  const [showPassword, setShowPassword] = useState(true)
  const [passwordError, setPasswordError] = useState("")
  const { organizationId: orgId, userId } = useParams()
  const {
    getUser,
    createUser,
    updateUser,
    getOrganizations,
    getUserForOrg,
    createUserForOrg,
    updateUserForOrg,
  } = useApi()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { addToast, isSuperAdmin } = useApp()

  const organizationsAsync = useAsync(async () => getOrganizations(0, 100), [])
  const organizations: Organization[] = organizationsAsync.result?.organizations || []

  const userAsync = useAsync(async () => {
    if (userId === "new" || !userId) return
    const user = !!orgId ? await getUserForOrg(orgId, userId) : await getUser(userId)
    setFormData({
      email: user.email,
      name: user.name,
      password: "",
      organization: user.organization?._id || "",
      role: user.organization?.role || "",
    })
    setShowPassword(false)
    return user
  }, [userId])

  const onSubmitAsync = useAsyncCallback(async () => {
    const payload: any = { ...formData }
    if (orgId) {
      payload.organization = orgId
    } else if (payload.organization) {
      payload.organization = [payload.organization]
    } else {
      payload.organization = []
    }
    delete payload.password2

    let result
    if (userAsync.result) {
      result = !!orgId
        ? await updateUserForOrg(orgId, userAsync.result._id, payload)
        : await updateUser(userAsync.result._id, payload)
      addToast("Success", "User data saved", "success")
    } else {
      const result = !!orgId ? await createUserForOrg(orgId, payload) : await createUser(payload)
      addToast("Success", "User created", "success")
      navigate(pathname.replace("new", result._id))
    }
    return result
  })

  const onSubmit = (e: any) => {
    e.preventDefault()
    if (onSubmitAsync.loading) return

    if (showPassword && formData.password !== formData.password2) {
      return setPasswordError("Passwords don't match")
    }

    setPasswordError("")

    onSubmitAsync.execute()
  }

  return (
    <div className="card">
      <div className="text-lg font-semibold text-gray-900">{userId === "new" ? "Create" : "Edit"} user</div>

      <form onSubmit={onSubmit}>
        <div className="mt-3">
          <label className="label">Email</label>
          <input
            className="input"
            name="email"
            type="email"
            placeholder="john@mail.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.currentTarget.value })}
            required
          />
        </div>

        {userAsync.result && (
          <div className="mt-3">
            <label htmlFor="changePassword" className="label">
              Change password
            </label>
            <input
              className="checkbox"
              type="checkbox"
              name="changePassword"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.currentTarget.checked)}
            />
          </div>
        )}

        {showPassword && (
          <>
            <div className="mt-3">
              <label className="label">Password</label>
              <input
                className="input"
                name="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.currentTarget.value })}
                required
              />
            </div>

            <div className="mt-3">
              <label className="label">Repeat password</label>
              <input
                className="input"
                name="password2"
                type="password"
                value={formData.password2}
                onChange={(e) => setFormData({ ...formData, password2: e.currentTarget.value })}
                required
              />
              {passwordError && <div className="error-message">{passwordError}</div>}
            </div>
          </>
        )}

        <div className="mt-3">
          <label className="label">Name</label>
          <input
            className="input"
            name="name"
            type="text"
            placeholder="John Smith"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value })}
            required
          />
        </div>

        {isSuperAdmin && !orgId && (
          <div className="mt-3">
            <label className="label">Organization</label>
            <select
              className="input"
              name="organization"
              value={formData.organization}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  organization: e.currentTarget.value,
                })
              }
              required
            >
              <option>Select one</option>
              {organizations.map((organization) => (
                <option key={organization._id} value={organization._id}>
                  {organization.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-3">
          <label className="label">Role</label>
          <select
            className="input"
            name="role"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.currentTarget.value })}
            required
          >
            <option value="">Select a role</option>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className="mt-4 flex items-center justify-end gap-x-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate(pathname.substring(0, pathname.lastIndexOf("/")))}
          >
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </div>
  )
}
